"use client"

import clsx from "clsx"
import {
  npPageGapClassName,
  npPageMtClassName,
  npPagePxClassName,
  npSectionMtClassName,
} from "src/components/np-design/components/constants"
import { NpHeading } from "src/components/np-design/components/NpSubtitle"
import { NpUppercaseHeading } from "src/components/np-design/components/NpUppercaseHeading"
import { Img } from "src/components/unstyled/Img"
import { CarouselSlideData } from "src/data/product-details/types"
import { getPlaceholderUrl, ImageCdnOptions, ImageFit, toImageCdnUrl } from "src/utilities/images"

const imageSizes = [1, 1.5, 2].map<ImageCdnOptions>((multiplier) => ({
  w: multiplier * 400,
  h: multiplier * 400,
  fit: "cover",
}))

const imageSizeMin = imageSizes.at(0)!
const imageSizeMax = imageSizes.at(-1)!

export type NpFeatureGridSectionProps = {
  capitalText?: string
  heading?: string
  slides: CarouselSlideData[]
  fit?: ImageFit
  aspectRatio?: `${string}/${string}`
}

export const NpFeatureGridSection = ({ capitalText, heading, slides, fit, aspectRatio }: NpFeatureGridSectionProps) => {
  fit ||= "cover"

  return (
    <section
      className={clsx(
        npSectionMtClassName,
        npPagePxClassName,
        npPageGapClassName,
        "flex max-w-full flex-col items-center",
      )}
    >
      {capitalText && <NpUppercaseHeading as={heading ? "p" : "h2"}>{capitalText}</NpUppercaseHeading>}

      {heading && <NpHeading as="h2">{heading}</NpHeading>}

      <div
        className={clsx(
          npPageGapClassName,
          npPageMtClassName,
          "grid grid-cols-2 items-stretch justify-center gap-2 px-4 md:grid-cols-3 md:gap-4 xl:gap-6",
          {
            "rounded-3xl": !slides.some((slide) => slide.captionPrimary),
          },
        )}
      >
        {slides.map((slide, index) => {
          const { captionPrimary, captionSecondary } = slide
          const rawSrc = slide.src || getPlaceholderUrl(imageSizeMax)
          const src = toImageCdnUrl(rawSrc, { ...imageSizeMin, fit })
          const srcSet = imageSizes.map((size) => `${toImageCdnUrl(rawSrc, { ...size, fit })} ${size.w}w`).join(", ")
          const sizes = imageSizeMin.w + "px"

          return (
            <div key={index} className="w-100 max-w-full">
              <div className="flex w-full flex-col items-center gap-4">
                <Img
                  src={src}
                  srcSet={srcSet}
                  sizes={sizes}
                  alt={captionPrimary || ""}
                  className={clsx("size-full rounded-3xl", fit === "cover" ? "object-cover" : "object-contain")}
                  style={{
                    aspectRatio: aspectRatio || "1/1",
                  }}
                />

                {captionPrimary && (
                  <div className="flex flex-col items-center gap-2 px-4 text-center leading-normal">
                    <div className="font-bold text-balance">{captionPrimary}</div>
                    {captionSecondary && <div className="text-balance">{captionSecondary}</div>}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
